import { supabase } from '@/lib/supabase/client';
import type { Tables } from '@/lib/supabase/types';

export type DocumentVersion = Tables<'document_versions'>;

export const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] } as const;

export async function getVersionsByDocument(documentId: string): Promise<DocumentVersion[]> {
  const { data, error } = await supabase
    .from('document_versions')
    .select('*')
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .order('version_number', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getDraftVersion(documentId: string): Promise<DocumentVersion | null> {
  const { data, error } = await supabase
    .from('document_versions')
    .select('*')
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .is('published_at', null)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Update the content of an existing draft version. Returns null (without throwing)
// if the version has already been published — this silently no-ops rather than
// surfacing a confusing "Save failed" message when the user publishes mid-debounce.
export async function saveDraftContent(
  versionId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TipTap JSON is untyped
  content: Record<string, any>,
): Promise<DocumentVersion | null> {
  const { data, error } = await supabase
    .from('document_versions')
    .update({ content_jsonb: content })
    .eq('id', versionId)
    .is('published_at', null)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data; // null → version is published; caller should treat as a no-op
}

// Publish a version: calls the existing publish_version RPC.
// RPC: sets published_at on the version and flips documents.current_version_id.
export async function publishVersion(versionId: string): Promise<void> {
  const { error } = await supabase.rpc('publish_version', { p_version_id: versionId });
  if (error) throw error;
}

// Fetch the currently-published version for a document (via current_version_id).
// Returns null if the document has no published version yet.
export async function getPublishedVersion(documentId: string): Promise<DocumentVersion | null> {
  const { data: doc, error: docError } = await supabase
    .from('documents')
    .select('current_version_id')
    .eq('id', documentId)
    .single();
  if (docError) throw docError;
  if (!doc.current_version_id) return null;

  const { data, error } = await supabase
    .from('document_versions')
    .select('*')
    .eq('id', doc.current_version_id)
    .single();
  if (error) throw error;
  return data;
}

// Get the current draft for a document, or create a new one if all versions are
// published. Multi-step → calls the get_or_create_draft RPC.
export async function getOrCreateDraft(documentId: string): Promise<DocumentVersion> {
  const { data, error } = await supabase.rpc('get_or_create_draft', {
    p_document_id: documentId,
  });
  if (error) throw error;
  return data as unknown as DocumentVersion;
}
